import { Dispatch, JSX, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Button from "@/components/buttons/Button.tsx";

import { Session } from "@/api/auth/auth.types.ts";
import { queryClient } from "@/api/query-client";
import { ROUTES } from "@/config/routes.ts";
import { LOGIN_KEY, useDeleteSession } from "@/hooks/api/login.hooks.ts";
import useModalRouter from "@/hooks/useModalRouter";
import { onError } from "@/utils/onError";

interface SessionInfoProps extends Pick<Session, "my_key" | "ua_name" | "updated"> {
  token: string;
  isDisabled: boolean;
  setIsDisabled: Dispatch<SetStateAction<boolean>>;
}

const SessionInfo = ({ my_key, ua_name, updated, token, isDisabled, setIsDisabled }: SessionInfoProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const date = new Date(updated);
  const { closeModal } = useModalRouter();

  const formattedDate = date.toLocaleDateString("ru", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

  const { mutate, isPending } = useDeleteSession();

  const handleDelete = () => {
    mutate(
      { token, key: my_key },
      {
        onSuccess: async (data) => {
          if (data.detail == "session_deleted_but_current_inactive") {
            queryClient.invalidateQueries({ queryKey: LOGIN_KEY.getSessions(token) });
            onError({ message: t("delete-another-session") });
            return;
          }
          setIsDisabled(true);
          const { setToken } = await import("@/api/tokens.ts");
          setToken(token);
          closeModal();
          queryClient.invalidateQueries({ queryKey: LOGIN_KEY.profileList });
          navigate(ROUTES.GATE.PROFILES);
        },
        onError,
      },
    );
  };

  return (
    <div
      className={
        "flex items-center justify-between gap-20 rounded-[0.938rem] bg-black-1 p-5 max-lg:gap-6 max-md:flex-col max-md:px-5 max-md:py-4"
      }
    >
      <div className={"space-y-[0.313rem] text-nowrap text-center max-md:space-y-1"}>
        <p className={"text-lg font-semibold leading-6"}>{ua_name}</p>
        <p className={"text-sm leading-[1.125rem] text-grey-1"}>
          {t("General.time-date")}: {formattedDate}
        </p>
      </div>
      <Button
        color={"white"}
        onClick={handleDelete}
        aria-label={t("Sessions.end")}
        disabled={isPending || isDisabled}
        className={"max-w-[14.375rem] max-sm:max-w-none"}
      >
        {t("Sessions.end")}
      </Button>
    </div>
  );
};

export default SessionInfo;
